import { useState, useEffect } from "react";
import {
  Collapse,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useApiFetch } from "../api/apiClient";
import { colors } from "../utils/styles";
import { fmtDate } from "../utils/dateFormat";

// Helper to format numbers with thousand separators
const formatNumber = (num) => {
  return new Intl.NumberFormat("en-US").format(num);
};

export default function ArchiveProcess() {
  const apiFetch = useApiFetch();

  const [activePeriod, setActivePeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Archive history state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyCollapsed, setHistoryCollapsed] = useState(true);

  const tables = [
    "ADM_FUNDING_AGENCIES",
    "AUTHORIZED_SIGNER",
    "PARENT_JURISDICTION_GROUP",
    "RM_JURISDICTION_ACCESS_LEVELS",
    "RM_JURISDICTION_CONTACTS",
    "RM_JURISDICTIONS_WITH_DISTRICTS_WITH_FUNDING_AGENCIES",
    "AgreementRoutingRecipient"
  ];

  useEffect(() => {
    loadActivePeriod();
    loadArchiveHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadActivePeriod = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiFetch("/submission-period/active");
      if (res.ok) {
        const data = await res.json();
        setActivePeriod(data);
      } else if (res.status === 404) {
        setError("No active submission period found. Please create a submission period first.");
      } else {
        throw new Error(`HTTP ${res.status}`);
      }
    } catch (err) {
      setError(err.message || "Failed to load active submission period");
    } finally {
      setLoading(false);
    }
  };

  // Extract year from TaxRollYear (e.g., "2026-27" -> 2026)

  const loadArchiveHistory = async () => {
    try {
      setHistoryLoading(true);
      const res = await apiFetch("/admin/archive-process/history");
      if (res.ok) {
        const data = await res.json();
        setHistory(data);
      }
    } catch (err) {
      console.error("Failed to load archive history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };
  const getDataYear = () => {
    if (!activePeriod?.taxRollYear) return null;
    const match = activePeriod.taxRollYear.match(/^(\d{4})/);
    return match ? parseInt(match[1], 10) : null;
  };

  const openConfirmDialog = () => {
    if (!activePeriod) return;
    setConfirmOpen(true);
  };

  const closeConfirmDialog = () => {
    if (executing) return;
    setConfirmOpen(false);
  };

  const handleExecute = async () => {
    const dataYear = getDataYear();
    if (!dataYear) {
      setError("Unable to determine data year from active submission period");
      return;
    }

    setExecuting(true);
    setError(null);
    setResult(null);
    setConfirmOpen(false);

    try {
      const res = await apiFetch("/admin/archive-process/copy-to-history", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dataYear })
      });

      if (!res.ok) {
        if (res.status === 409) {
          // Handle 409 Conflict (archive already exists)
          const errorData = await res.json().catch(() => ({}));
          if (errorData.errorMessage) {
            throw new Error(errorData.errorMessage);
          }
        }
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setResult(data);
      // Reload active period and archive history to reflect updated status
      loadActivePeriod();
      loadArchiveHistory();
    } catch (err) {
      setError(err.message || "Archive process failed.");
    } finally {
      setExecuting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: "100%", mt: 2, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const dataYear = getDataYear();

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Typography variant="h4" gutterBottom>
        Archive Process
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          This operation creates a point-in-time snapshot of reference and master data by copying current data
          to history tables. The first archive will close submissions for the period. Additional archives can
          be run for periodic snapshots (limited to once per day).
        </Typography>

        {activePeriod ? (
          <>
            <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Active Submission Period
              </Typography>
              <Typography variant="body2">
                <strong>Tax Roll Year:</strong> {activePeriod.taxRollYear}
              </Typography>
              <Typography variant="body2">
                <strong>Archive Data Year:</strong> {dataYear}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong>{" "}
                {activePeriod.isArchived ? (
                  <span style={{ color: colors.warning }}>
                    Archived (submissions closed, archives allowed)
                  </span>
                ) : (
                  <span style={{ color: colors.success }}>Open for submissions</span>
                )}
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
            {/* Archive History Section */}
            {history.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box
                  onClick={() => setHistoryCollapsed(!historyCollapsed)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#f5f5f5" },
                    p: 1,
                    borderRadius: 1,
                    mb: 1, maxWidth: 800
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                    Previous Archives ({history.length})
                  </Typography>
                  {historyCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </Box>
                <Collapse in={!historyCollapsed}>
                  {historyLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <TableContainer component={Paper} sx={{ maxWidth: 800 }}>
                      <Table size="small">
                        <TableHead
                          sx={{
                            "& .MuiTableCell-head": {
                              bgcolor: colors.darkGray,
                              color: "white",
                              fontWeight: 700
                            }
                          }}
                        >
                          <TableRow>
                            <TableCell sx={{ width: 150 }}>Tax Roll Year</TableCell>
                            <TableCell sx={{ width: 180 }}>Snapshot Date</TableCell>
                            <TableCell>Executed By</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {history.map((entry) => (
                            <TableRow key={entry.id} hover>
                              <TableCell>{entry.taxRollYear || "—"}</TableCell>
                              <TableCell>{fmtDate(entry.snapshotDate)}</TableCell>
                              <TableCell>{entry.executedByName || entry.executedBy}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Collapse>
              </Box>
            )}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Tables to be Archived ({tables.length})
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
                {tables.map((table) => (
                  <li key={table}>
                    <Typography variant="body2">{table}</Typography>
                  </li>
                ))}
              </Box>
            </Box>

            {!activePeriod.isArchived && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <strong>Important:</strong> This is the first archive for this period. Running this will close
                submissions. Additional archives can still be run for periodic snapshots.
              </Alert>
            )}
          </>
        ) : null}

        <Button
          variant="contained"
          startIcon={executing ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
          onClick={openConfirmDialog}
          disabled={executing || !activePeriod}
          sx={{ alignSelf: "flex-start" }}
        >
          {executing ? "Processing..." : "Execute Archive Process"}
        </Button>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {result && result.success && (
        <Paper sx={{ p: 3 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Archive process completed successfully for year {result.dataYear}
            </Typography>
            <Typography variant="body2">
              <strong>Snapshot Date:</strong> {result.snapshotDate}
            </Typography>
            <Typography variant="body2">
              <strong>Executed By:</strong> {result.executedByName || result.executedBy}
            </Typography>
          </Alert>

          <TableContainer>
            <Table size="small">
              <TableHead
                sx={{ "& .MuiTableCell-head": { bgcolor: colors.darkGray, color: "white", fontWeight: 700 } }}
              >
                <TableRow>
                  <TableCell>Table Name</TableCell>
                  <TableCell align="right">Rows Copied</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.tableResults.map((t) => (
                  <TableRow key={t.tableName} hover>
                    <TableCell>{t.tableName}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                      {formatNumber(t.rowsCopied)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                    {formatNumber(result.totalRowsCopied)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={confirmOpen} onClose={closeConfirmDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Archive Process</DialogTitle>
        <DialogContent>
          <Typography variant="body1">Are you sure you want to execute the archive process?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} disabled={executing}>
            Cancel
          </Button>
          <Button onClick={handleExecute} variant="contained" color="primary" disabled={executing}>
            {executing ? "Processing..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
